import { selectIsLoadingDashboard } from "@/store/reducers/dashboard/dashboard.selector";
import { Icon, InlineIcon } from "@iconify/react";
import { Pagination, Skeleton, TextField } from "@mui/material";
import * as FileSaver from "file-saver";
import _ from "lodash";
import moment from "moment";
import React, { useState } from "react";
import { ConnectedProps, connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import styled from "styled-components";
import * as XLSX from "xlsx";
import "./CustomTable.style.scss";

type TCustomTableProps = {
  headers: any[];
  rows: any[] | null;
  title: string;
  rowsPerPage?: number;
  headerColor?: string;
  textColor?: string;
  colsToExport?: string;
  titleExport?: string;
  searchTerm?: string;
  handleSearchChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  table?: string;
  // Server-side pagination props
  totalCount?: number;
  page?: number;
  onPageChange?: (event: any, newPage: number) => void;
  // External export function (returns promise resolving to data)
  onExport?: () => Promise<any[]>;
};

type PropsFromRedux = ConnectedProps<typeof connector>;

const CustomTableFct: React.FC<TCustomTableProps & PropsFromRedux> = ({
  headers,
  rows,
  title,
  rowsPerPage = 7,
  headerColor,
  loading,
  textColor,
  colsToExport,
  titleExport,
  searchTerm,
  handleSearchChange,
  table,
  totalCount,
  page: propPage,
  onPageChange,
  onExport,
}) => {
  const [order, setOrder] = useState<boolean | undefined>(true);
  const [sortBy, setSortBy] = useState<string | undefined>("");
  // const [search, setSearch] = useState<string | undefined>("");
  const [isExporting, setIsExporting] = useState(false);

  const [internalPage, setInternalPage] = useState(1);
  // const columnRef = rows[0];

  const currentPage = propPage !== undefined ? propPage : internalPage;

  const pageChange = (event: any, value: any) => {
    if (onPageChange) {
      onPageChange(event, value);
    } else {
      setInternalPage(value);
    }
  };

  const newRows = rows !== null ? rows : [];

  let list = newRows;
  // Only slice locally if NOT using server-side pagination (indicated by presence of totalCount)
  if (totalCount === undefined) {
    list = newRows.slice(
      (currentPage - 1) * rowsPerPage,
      (currentPage - 1) * rowsPerPage + rowsPerPage
    );
  }

  console.log("list in tableau ", list);

  const handleASC = (id: number) => {
    if (rows !== null) {
      setOrder(true);
      const value = Object.entries(rows[0]);
      if (value[id][0] === sortBy) {
        setSortBy("date");
      } else {
        setSortBy(value[id][0]);
      }
    }
  };

  const handleDESC = (id: number) => {
    if (rows !== null) {
      setOrder(false);
      const value = Object.entries(rows[0]);
      if (value[id][0] === sortBy) {
        setSortBy("date");
      } else {
        setSortBy(value[id][0]);
      }
    }
  };

  const numberFormatter = (str: any): any => {
    // if (typeof str !== 'string') {
    //   return str;
    // }

    // const num = Number(str);

    // if (Number.isInteger(num)) {
    //   return new Intl.NumberFormat('fr-FR').format(num) ;
    // }

    // return str;

    if (!str && str !== 0) return;

    var nombre = str;
    nombre += "";

    // Ne pas grouper par milliers les valeurs mixtes texte+chiffres (ex: "Juin 2026") :
    // uniquement les nombres purs (entiers, signe négatif optionnel).
    if (!/^-?\d+$/.test(nombre)) return nombre;

    var sep = "  ";
    var reg = /(\d+)(\d{3})/;

    while (reg.test(nombre)) {
      nombre = nombre.replace(reg, "$1" + sep + "$2");
    }

    return nombre;
  };

  // function ExportToExcel() {
  //     downloadExcel({
  //       fileName: title + "_" + moment().format("YYYYMMDD"),
  //       sheet: "Feuille",
  //       tablePayload: {
  //         header: headers,
  //         // accept two different data structures
  //         body: rows || [],
  //       }
  //     });
  // }

  /*** TEST EXPORT */
  const fileType =
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
  const fileExtension = ".xlsx";

  const exportToCSV = async (currentRows: any) => {
    setIsExporting(true);
    try {
      let rowsToExport = currentRows;

      if (onExport) {
        rowsToExport = await onExport();
      }

      let _cols = colsToExport?.split(",");
      // const ws = XLSX.utils.json_to_sheet(rows);
      const ws = sheet_from_array_of_arrays(rowsToExport, _cols);
      const wb = { Sheets: { data: ws }, SheetNames: ["data"] };
      const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const data = new Blob([excelBuffer], { type: fileType });
      FileSaver.saveAs(
        data,
        titleExport + "" + moment().format("YYYYMMDD") + fileExtension
      );
    } catch (error) {
      console.error("Export failed", error);
    } finally {
      setIsExporting(false);
    }
  };

  const sheet_from_array_of_arrays = (data: any, cols: any) => {
    let ws: any = {};
    var range = {
      s: { c: 0, r: 0 },
      e: { c: cols.length - 1, r: data.length },
    };
    var codeAsciiInit = 64; //POUR SYMBOLISER grd A

    for (var R = 0; R != data.length + 1; ++R) {
      _.forEach(cols, (v: any, C: any) => {
        if (range.s.r > R) range.s.r = R;
        if (range.s.c > C) range.s.c = C;
        if (range.e.r < R) range.e.r = R;
        if (range.e.c < C) range.e.c = C;

        var cell;
        var cell_ref;
        v = cols[C].split("|")[0].trim();

        if (R == 0) {
          var header = "";

          header = cols[C].split("|")[1].trim() || "";
          // header   = cols[C].split('|')[1].trim() || cols[C].trim();

          // cell = String.fromCharCode(codeAsciiInit + (C+1))+"1";
          // console.log("cellule",cell);
          cell = { v: header, t: "s" };

          cell.t = "s";
          cell_ref = XLSX.utils.encode_cell({ c: C, r: R });
          ws[cell_ref] = cell;
        } else {
          cell = { v: data[R - 1][v], t: "", z: "" };

          if (cell.v != null) {
            cell_ref = XLSX.utils.encode_cell({ c: C, r: R });

            if (typeof cell.v === "number") cell.t = "n";
            else if (typeof cell.v === "boolean") cell.t = "b";
            else if (cell.v instanceof Date) {
              cell.t = "n";
              cell.z = XLSX.SSF._table[14];
              cell.v = datenum(cell.v);
            } else cell.t = "s";

            ws[cell_ref] = cell;
          }
        }
      });

      // cols.forEach(cols, (v : any, C : any) => {

      // });
    }
    if (range.s.c < 10000000) ws["!ref"] = XLSX.utils.encode_range(range);

    return ws;
  };

  const datenum = (v: any, date1904?: any) => {
    var epoch = v.getTime();
    if (date1904) epoch += 1462 * 24 * 60 * 60 * 1000;
    return (epoch + 2209161600000) / (24 * 60 * 60 * 1000);
  };

  return (
    <>
      {loading && (
        <>
          <div className="skeleton">
            <Skeleton variant="text" animation="wave" className="sk-title" />
            <Skeleton
              variant="rectangular"
              animation="pulse"
              className="sk-input"
            />
            <Skeleton variant="rounded" animation="wave" className="sk-table" />
          </div>
        </>
      )}
      {!loading && rows !== null && (
        <div className="table-part">
          <div className="table-title">
            <span>{title}</span>
            <div className="option">
              <div className="option-item">
                {table === "users" && (
                  <TextField
                    id="search"
                    className="search-input"
                    value={searchTerm}
                    placeholder="Rechercher..."
                    variant="outlined"
                    size="small"
                    fullWidth
                    onChange={handleSearchChange}
                  />
                )}
              </div>
              <div className="option-item">
                {/* <button type='button' onClick={ExportToExcel} title={'Exporter'}>Exporter</button> */}
                <button
                  type="button"
                  onClick={(e) => {
                    exportToCSV(rows);
                  }}
                  title={"Exporter"}
                  disabled={isExporting}
                  style={{ opacity: isExporting ? 0.7 : 1, cursor: isExporting ? 'wait' : 'pointer' }}
                >
                  <Icon
                    icon="line-md:file-download"
                    width="20"
                    height="20"
                    color="var(--ui-black)"
                  />
                  {isExporting ? 'Export...' : 'Exporter'}
                </button>
              </div>
            </div>
          </div>
          <div className="table-container">
            <TableHeader
              className="table-header"
              color={headerColor}
              textColor={textColor}
            >
              {_.map(headers, (item: any, index: any) => (
                <div className={"header-item " + item.field} key={index}>
                  <span>{item.headerName}</span>
                  {item !== "" && (
                    <div className="group-arrow">
                      <InlineIcon
                        className="arrow"
                        icon="ant-design:caret-up-filled"
                        onClick={() => {
                          handleASC(index);
                        }}
                      />
                      <InlineIcon
                        className="arrow"
                        icon="ant-design:caret-down-filled"
                        onClick={() => {
                          handleDESC(index);
                        }}
                      />
                    </div>
                  )}
                </div>
              ))}
            </TableHeader>
            <div className="table-content">
              {_.map(
                _.orderBy(list, sortBy, order ? "asc" : "desc"),
                (item: any, index: any) => (
                  <div className="table-row" key={index}>
                    {Object.keys(list[0])?.map((value, index) => {
                      return (
                        <span key={index}>
                          {!(value === "actions")
                            ? value === "telephone"
                              ? item[value]?.toString()
                              : numberFormatter(item[value]?.toString())
                            : item[value](item["id"])}
                        </span>
                      );
                    })}
                  </div>
                )
              )}
            </div>
          </div>
          <div className="pagination">
            <small>
              {" "}
              <b>{totalCount !== undefined ? totalCount : rows.length}</b> lignes{" "}
            </small>
            <Pagination
              count={Math.ceil((totalCount !== undefined ? totalCount : rows.length) / rowsPerPage)}
              page={currentPage}
              onChange={pageChange}
              showFirstButton
              showLastButton
              variant="outlined"
              shape="rounded"
              color="primary"
            />
          </div>
        </div>
      )}
      {rows == null && (
        <>
          <p> Aucune donnée </p>
        </>
      )}
    </>
  );
};

const TableHeader = styled.div<{ color?: string; textColor?: string }>`
  background: ${({ color }) => {
    return color;
  }} !important;
  .header-item {
    color: ${({ textColor }) => {
    return textColor;
  }} !important;
  }
`;

const mapStateToProps = createStructuredSelector({
  loading: selectIsLoadingDashboard,
});

const connector = connect(mapStateToProps);
const CustomTable = connector(CustomTableFct);

export default CustomTable;
